import { Link } from 'react-router-dom';
import { useActivity, notifyInboxChanged } from '../hooks/useApi';
import { api, type ActivityEvent } from '../api/client';
import { Avatar } from '../components/AppShell';
import EmptyState from '../components/EmptyState';
import { relativeTime } from '../utils';


function renderEventBody(event: ActivityEvent, onLinkClick?: () => void) {
  return (
    <p className="m-0 text-[14px] leading-[1.5]" style={{ color: 'var(--rd-text-medium)' }}>
      <span
        className="font-semibold"
        style={{ color: 'var(--rd-text-strong)' }}
      >
        {event.actor.name}
      </span>{' '}
      {event.description}
      {event.artifact_id && event.artifact_title ? (
        <>
          {' '}
          <Link
            to={`/a/${event.artifact_id}`}
            className="font-semibold no-underline"
            style={{ color: 'var(--rd-accent)' }}
            onClick={onLinkClick}
          >
            {event.artifact_title}
          </Link>
        </>
      ) : null}
    </p>
  );
}

export default function ActivityFeed() {
  const { events, loading, error, refetch } = useActivity();

  const handleMarkAllRead = async () => {
    await api.markAllRead();
    refetch();
    notifyInboxChanged();
  };

  const handleMarkOneRead = async (id: string) => {
    await api.markOneRead(id);
    refetch();
    notifyInboxChanged();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div
          className="w-[32px] h-[32px] border-[3px] rounded-full animate-spin"
          style={{
            borderColor: 'var(--rd-border)',
            borderTopColor: 'var(--rd-text-medium)',
          }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <EmptyState
        icon="icon-app-warning"
        title="Could not load inbox"
        subtitle={error}
      />
    );
  }

  if (events.length === 0) {
    return (
      <EmptyState
        icon="icon-app-alerts-alt"
        title="Inbox is clear"
        subtitle="You'll see shares, comments, reactions, and admin join events here."
      />
    );
  }

  const unread = events.filter((event) => !event.read);
  const read = events.filter((event) => event.read);

  return (
    <div className="px-[var(--sp-6)] pt-[16px] pb-[var(--sp-8)]">
      <div className="flex items-center justify-between mb-[var(--sp-6)]">
        <div>
          <h1
            className="m-0 text-[22px] font-bold"
            style={{ color: 'var(--rd-text-strong)' }}
          >
            Inbox
          </h1>
          <p
            className="m-0 mt-[var(--sp-2)] text-[14px]"
            style={{ color: 'var(--rd-text-subtle)' }}
          >
            Shares, comments, reactions, and workspace join events that matter to you.
          </p>
        </div>
        {unread.length > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="h-[36px] px-[var(--sp-3)] rounded-[8px] text-[13px] font-semibold cursor-pointer border transition-colors duration-[120ms]"
            style={{
              background: 'transparent',
              borderColor: 'var(--rd-border)',
              color: 'var(--rd-text-medium)',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rd-surface-hover)')}
            onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
          >
            Mark all as read
          </button>
        )}
      </div>

      {unread.length > 0 && (
        <section className="mb-[var(--sp-7)]">
          <div className="mb-[var(--sp-3)]">
            <h2
              className="m-0 text-[13px] font-semibold uppercase tracking-[0.04em]"
              style={{ color: 'var(--rd-text-meta)' }}
            >
              Unread
            </h2>
          </div>
          <div className="flex flex-col gap-[var(--sp-2)]">
            {unread.map((event) => (
              <div
                key={event.id}
                className="group rounded-[8px] border p-[var(--sp-4)] transition-colors duration-[120ms]"
                style={{
                  background: 'var(--rd-surface-input)',
                  borderColor: 'var(--rd-border)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rd-surface-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--rd-surface-input)')}
              >
                <div className="flex items-start gap-[var(--sp-3)]">
                  <div
                    className="w-[8px] h-[8px] rounded-full flex-shrink-0 mt-[12px]"
                    style={{ background: '#EF4444' }}
                  />
                  <Avatar
                    name={event.actor.name}
                    avatarUrl={event.actor.avatar_url}
                    size={32}
                  />
                  <div className="flex-1 min-w-0">
                    {renderEventBody(event, () => handleMarkOneRead(event.id))}
                    <div
                      className="mt-[var(--sp-2)] text-[13px]"
                      style={{ color: 'var(--rd-text-faint)' }}
                    >
                      {relativeTime(event.created_at)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleMarkOneRead(event.id)}
                    className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity duration-[120ms] border-0 bg-transparent cursor-pointer rounded-[6px] w-[28px] h-[28px] flex items-center justify-center"
                    style={{ color: 'var(--rd-text-meta)' }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--rd-surface-panel)'; e.currentTarget.style.color = 'var(--rd-text-strong)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = 'var(--rd-text-meta)'; }}
                    title="Mark as read"
                    aria-label="Mark as read"
                  >
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M3 8.5L6.5 12L13 4" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {read.length > 0 && (
        <section>
          <div className="mb-[var(--sp-3)]">
            <h2
              className="m-0 text-[13px] font-semibold uppercase tracking-[0.04em]"
              style={{ color: 'var(--rd-text-meta)' }}
            >
              Earlier
            </h2>
          </div>
          <div className="flex flex-col gap-[var(--sp-2)]">
            {read.map((event) => (
              <div
                key={event.id}
                className="rounded-[8px] border p-[var(--sp-4)] transition-colors duration-[120ms]"
                style={{
                  background: 'var(--rd-surface-panel)',
                  borderColor: 'var(--rd-border)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--rd-surface-hover)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'var(--rd-surface-panel)')}
              >
                <div className="flex items-start gap-[var(--sp-3)]">
                  <Avatar
                    name={event.actor.name}
                    avatarUrl={event.actor.avatar_url}
                    size={32}
                  />
                  <div className="flex-1 min-w-0">
                    {renderEventBody(event)}
                    <div
                      className="mt-[var(--sp-2)] text-[13px]"
                      style={{ color: 'var(--rd-text-faint)' }}
                    >
                      {relativeTime(event.created_at)}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
